import { useRef } from 'react';
import { useUser } from '@auth0/nextjs-auth0';
import { Auth0UserInfo } from '@lib/auth0/utils';

/**
 * The Auth0 session user, typed from our custom Auth0 user object.
 *
 * This is the login state only. Membership roles and subscription are read
 * from ffh-backend by useAuth0User, because the claims in an Auth0 session are
 * only as fresh as the login that created it, and a session now lasts months
 * (FFH-3019). Use this hook where identity is all that is needed, and
 * useAuth0User everywhere else.
 *
 * A failed /auth/profile fetch is not a sign-out. SWR revalidates that route
 * on window focus and useUser reports any non-OK response as `user: null`
 * with `error` set, so a transient server or network failure mid-session
 * would otherwise read as logged out: the paywall flashes, gated pages
 * unmount (and lose their in-progress state) and remount once the retry
 * succeeds. The last known user is held across such errors. A genuine
 * sign-out resolves to null with no error and still clears the user.
 *
 * @returns {Object} An object containing:
 *   - user: The authenticated user information typed as Auth0UserInfo, or undefined if not authenticated
 *   - error: Any error that occurred during authentication
 *   - isLoading: Boolean indicating whether the user data is currently being loaded
 */
export const useAuth0SessionUser = () => {
  const { user, error, isLoading } = useUser();
  const lastKnownUserRef = useRef<Auth0UserInfo | undefined>(undefined);

  if (user) {
    lastKnownUserRef.current = user as Auth0UserInfo;
  } else if (!error) {
    // A settled logged-out response (204, no error) clears the memo, so a
    // later fetch failure cannot resurrect the expired session's user. This
    // is reachable without a remount: in-SPA session expiry leaves the hook
    // mounted with the user gone.
    lastKnownUserRef.current = undefined;
  }

  const isTransientFailure = !user && Boolean(error);
  const resolvedUser = isTransientFailure
    ? lastKnownUserRef.current
    : (user as Auth0UserInfo | undefined);

  return {
    user: resolvedUser,
    error,
    isLoading
  };
};
